/**
 * Incline / decline GRADIENT helpers for elevation profiles.
 *
 * A "gradient" (a.k.a. grade or slope) is the signed steepness of a segment:
 *
 *   grade% = rise / run * 100
 *
 * where `rise` is the elevation change (metres) and `run` is the horizontal
 * distance travelled (metres). Positive = incline (climbing), negative =
 * decline (descending). Because grade is a ratio of two lengths it is
 * unit-independent, so callers should always compute it in METRES (before any
 * big/small-unit conversion) and the result is the same regardless of the
 * user's measurement preference.
 *
 * Colours are on a diverging scale so incline vs decline read differently at a
 * glance: declines use cool blues, near-flat is a neutral grey, inclines use
 * warm oranges/reds. Blue↔orange/red is a colourblind-reasonable pairing.
 */

export interface GradientBucket {
  /** Short human label for a legend, e.g. `'↑ 6–9%'`. */
  label: string;
  /** Bucket colour (hex). */
  color: string;
  /** Inclusive lower bound of the signed grade % this bucket covers. */
  minPercent: number;
  /** Exclusive upper bound of the signed grade % this bucket covers. */
  maxPercent: number;
}

/** Neutral colour used for near-flat grades and non-finite input. */
export const GRADIENT_NEUTRAL_COLOR = '#9E9E9E';

/**
 * Diverging gradient scale, ordered steep-decline → flat → steep-incline.
 *
 * Break points are the classic |grade| buckets 0-3-6-9-12-15+%. Declines are
 * cool blues (darker = steeper), the flat band (|grade| < 3%) is neutral grey,
 * and inclines are warm oranges/reds (darker = steeper).
 */
export const GRADIENT_LEGEND: GradientBucket[] = [
  { label: '↓ 15%+', color: '#08519C', minPercent: -Infinity, maxPercent: -15 },
  { label: '↓ 12–15%', color: '#3182BD', minPercent: -15, maxPercent: -12 },
  { label: '↓ 9–12%', color: '#6BAED6', minPercent: -12, maxPercent: -9 },
  { label: '↓ 6–9%', color: '#9ECAE1', minPercent: -9, maxPercent: -6 },
  { label: '↓ 3–6%', color: '#C6DBEF', minPercent: -6, maxPercent: -3 },
  { label: 'Flat', color: GRADIENT_NEUTRAL_COLOR, minPercent: -3, maxPercent: 3 },
  { label: '↑ 3–6%', color: '#FED976', minPercent: 3, maxPercent: 6 },
  { label: '↑ 6–9%', color: '#FEB24C', minPercent: 6, maxPercent: 9 },
  { label: '↑ 9–12%', color: '#FD8D3C', minPercent: 9, maxPercent: 12 },
  { label: '↑ 12–15%', color: '#F03B20', minPercent: 12, maxPercent: 15 },
  { label: '↑ 15%+', color: '#BD0026', minPercent: 15, maxPercent: Infinity },
];

/**
 * Signed grade % between two samples: `rise / run * 100`.
 *
 * Positive = incline, negative = decline. Guards divide-by-zero (returns 0 when
 * the horizontal run is ~0 or non-finite) so co-located / duplicate points
 * don't produce Infinity.
 */
export function gradientPercentBetween(
  distanceMeters1: number,
  ele1: number,
  distanceMeters2: number,
  ele2: number
): number {
  const run = distanceMeters2 - distanceMeters1;
  if (!Number.isFinite(run) || Math.abs(run) < 1e-9) return 0;
  const rise = ele2 - ele1;
  if (!Number.isFinite(rise)) return 0;
  return (rise / run) * 100;
}

/**
 * Per-point signed grade % for parallel distance/elevation arrays (metres).
 *
 * Point `i` gets the grade of the segment from `i` to `i+1`; the last point
 * repeats the previous point's grade so the returned array length always
 * matches the input length (handy for keying a Chart.js `segment.borderColor`
 * callback off the sampled point index).
 */
export function computeSegmentGradients(
  distancesMeters: number[],
  elevationsMeters: number[]
): number[] {
  const n = distancesMeters.length;
  if (n === 0) return [];
  if (n === 1) return [0];

  const out: number[] = new Array(n);
  for (let i = 0; i < n - 1; i++) {
    out[i] = gradientPercentBetween(
      distancesMeters[i],
      elevationsMeters[i],
      distancesMeters[i + 1],
      elevationsMeters[i + 1]
    );
  }
  // Last point has no following segment; repeat the previous grade.
  out[n - 1] = out[n - 2];
  return out;
}

/**
 * Sustained per-point grade %, evaluated over a fixed DISTANCE WINDOW instead
 * of one sample-to-sample segment.
 *
 * Segment-to-segment grades are only as wide as the sampling interval
 * (routeLength / maxPoints — as little as a few metres on short routes), so
 * ordinary elevation jitter reads as ±20% spikes. Difficulty as a hiker
 * experiences it is the grade SUSTAINED over a stretch of trail, so each
 * sampled point gets the average grade of the `windowMeters` of full-
 * resolution track centred on it: `(ele(end) − ele(start)) / (end − start)`.
 *
 * The window never crosses a DISCONTINUITY — a spot where distance stalls but
 * elevation jumps (adjacent full-res points with ~zero run and a large rise).
 * That's the signature of a multi-stage plan's day boundary, where the gap
 * between one stage's end and the next stage's start is deliberately not
 * counted as hiked distance; averaging across it would manufacture an absurd
 * grade out of the two trailheads' altitude difference.
 *
 * @param fullDistances  Full-resolution cumulative distance (metres),
 *                       non-decreasing.
 * @param fullElevations Full-resolution elevations (metres), parallel.
 * @param sampleIndices  Indices into the full-resolution arrays for each
 *                       charted point (from equalDistanceSampleIndices).
 * @param windowMeters   Window width. Internally clamped to a tenth of the
 *                       route (30 m floor) so short loops keep their detail.
 */
export function computeSustainedGradients(
  fullDistances: number[],
  fullElevations: number[],
  sampleIndices: number[],
  windowMeters: number
): number[] {
  const n = fullDistances.length;
  if (n === 0 || sampleIndices.length === 0) return sampleIndices.map(() => 0);
  if (n === 1) return sampleIndices.map(() => 0);

  const totalDistance = fullDistances[n - 1] - fullDistances[0];
  const effectiveWindow = Math.min(windowMeters, Math.max(totalDistance / 10, 30));
  const halfWindow = effectiveWindow / 2;

  // Discontinuities: ~zero horizontal run with a big elevation jump. 10 m of
  // rise over no distance is not terrain any hiker walked.
  const DISCONTINUITY_MAX_RUN_M = 0.001;
  const DISCONTINUITY_MIN_RISE_M = 10;
  const isBreakAfter = (i: number): boolean =>
    fullDistances[i + 1] - fullDistances[i] < DISCONTINUITY_MAX_RUN_M &&
    Math.abs(fullElevations[i + 1] - fullElevations[i]) >= DISCONTINUITY_MIN_RISE_M;

  return sampleIndices.map((si) => {
    const center = fullDistances[si];
    const minDistance = center - halfWindow;
    const maxDistance = center + halfWindow;

    // Walk outward from the sample; stop at the window edge or a break. The
    // FIRST step in each direction is unconditional (subject only to the
    // break/array-bounds check) — on tracks whose raw point spacing exceeds
    // the window (common: many GPX tracks sample every 50-100m), a strict
    // window-only walk would find zero neighbours and silently report 0%
    // everywhere. Guaranteeing one step degrades gracefully to a plain
    // segment-to-segment grade in that case instead of going flat.
    let lo = si;
    while (lo > 0 && !isBreakAfter(lo - 1) && (lo === si || fullDistances[lo - 1] >= minDistance)) {
      lo--;
    }
    let hi = si;
    while (hi < n - 1 && !isBreakAfter(hi) && (hi === si || fullDistances[hi + 1] <= maxDistance)) {
      hi++;
    }

    return gradientPercentBetween(
      fullDistances[lo],
      fullElevations[lo],
      fullDistances[hi],
      fullElevations[hi]
    );
  });
}

/**
 * Map a signed grade % to a diverging-scale colour (see {@link GRADIENT_LEGEND}).
 * Non-finite input resolves to the neutral flat colour.
 */
export function gradientColorForPercent(percent: number): string {
  const p = Number.isFinite(percent) ? percent : 0;
  for (const bucket of GRADIENT_LEGEND) {
    if (p >= bucket.minPercent && p < bucket.maxPercent) return bucket.color;
  }
  return GRADIENT_NEUTRAL_COLOR;
}
